import React from "react"
import styled from "styled-components"
import { Button, Card, CardBody, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import Select from "react-select"
import PropTypes from "prop-types"
import { getUID } from "echarts/lib/util/component"
import { getCohortReportFilterData } from "../../../../helpers/jwt_helper"
import DefaultMultipleSelect from "../../../../components/DefaultMultipleSelect/DefaultMultipleSelect"

const CohortReportsSettingPanel = ({ initialData, options, onChange }) => {

  const getValidData = dt => {
    dt = JSON.parse(JSON.stringify(dt))
    dt.group_by = dt.group_by.map(i => ({ value: i, key: getUID() }))
    dt.filter_by = dt.filter_by.map(i => ({
      value: i.value,
      key: getUID(),
      sub_options: i.sub_options || [],
      sub_value: i.sub_value,
      isLoading: false
    }))

    dt.app = options.apps.find(i => i.value === initialData.app_id)
    return dt
  }

  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const [data, setData] = React.useState(getValidData(initialData))

  React.useEffect(() => {
    setData(getValidData(initialData))
  }, [initialData, isModalVisible])

  const onCloseModal = () => setIsModalVisible(false)

  const onSubmitModal = () => {
    const dt = JSON.parse(JSON.stringify(data))
    dt.group_by = dt.group_by.map(i => i?.value).filter(i => !!i)

    if (dt.group_by.length === 0) {
      dt.group_by = initialData.group_by
    }

    dt.filter_by = dt.filter_by.map(({ value, sub_value, sub_options }) => ({
      value,
      sub_value,
      sub_options
    })).filter(i => i.value && i.sub_value.length > 0)

    dt.app_id = dt.app.value
    delete dt.app
    onChange(dt)
    setIsModalVisible(false)
  }

  const onChangeRadioBtn = (e, i) => {
    setData(prev => ({
      ...prev,
      [e.target.name]: i
    }))
  }

  const patchByKey = (name, key, params = {}) => {
    setData(prev => {
      return {
        ...prev,
        [name]: prev[name].map(i => i.key !== key ? i : ({ ...i, ...params }))
      }
    })
  }

  const onChangeSelect = async (value, i, name) => {

    if (name === "filter_by") {
      try {

        patchByKey(name, i.key, { isLoading: true, value })

        let sub_options = await getCohortReportFilterData({
          field: value.value,
          app_id: initialData.app_id
        })
        sub_options = sub_options.map(i => ({ value: i, label: i, checked: false }))
        patchByKey(name, i.key, { isLoading: false, sub_options, sub_value: undefined })
      } catch (e) {

      }
      return void 0
    }

    if (name === "filter_by__sub") {
      patchByKey("filter_by", i.key, { sub_value: value })
    }

    if (name === "group_by") {
      patchByKey(name, i.key, {
        value
      })
      return void 0
    }
  }

  const handleAddRow = (name) => {

    const emptyData = () => name === "group_by" ? ({ key: getUID() }) : ({
      value: undefined,
      key: getUID(),
      sub_options: [],
      sub_value: undefined,
      isLoading: true
    })

    const getInitRowData = () => {
      const value = options[name].find(i => !(data[name].some(el => el.value.value === i.value)))
      return { ...emptyData(), value }
    }

    const row = getInitRowData()

    if (row.value) {
      setData(prev => {
        return {
          ...prev,
          [name]: [...prev[name], row]
        }
      })

      if (name === "filter_by") {
        onChangeSelect(row.value, row, name)
          .then()
      }
    }

    // if (row) {
    //   setData(prev => {
    //     return {
    //       ...prev,
    //       [name]: [...prev[name], row]
    //     }
    //   })
    // }
  }

  const handleRemoveRow = (name, i) => {
    setData(prev => ({
      ...prev,
      [name]: prev[name].filter(el => el.key !== i.key)
    }))
  }

  const onSelectSubFilterBy = (v, i) => {
    patchByKey("filter_by", i.key, { sub_value: v })
  }

  const getOptions = (name, item) => {
    return options[name].filter(i => (i.value === item.value?.value) || !(data[name].some(el => (i.value === el.value?.value))))
  }


  const app_label = options.apps.find(i => i.value === initialData.app_id)?.label // || initialData.app_id

  return (
    <React.Fragment>
      <Modal
        isOpen={isModalVisible}
        autoFocus={true}
        centered={true}
        toggle={onCloseModal}
        style={{ maxWidth: 615 }}
      >
        <ModalHeader tag="h4" toggle={onCloseModal}>Cohort setup</ModalHeader>
        <ModalBodyStyled>
          <div className="mb-3">
            <h6>Cohort type</h6>
            <div className="btn-group" role="group">
              {options.cohort_type.map(i => (
                <React.Fragment key={i.value}>
                  <input type="radio"
                         className="btn-check"
                         name="cohort_type"
                         id={i.value}
                         value={i.value}
                         disabled={i.disabled}
                         checked={data.cohort_type.value === i.value}
                         onChange={e => onChangeRadioBtn(e, i)}
                  />
                  <label className="btn btn-outline-primary btn-sm"
                         htmlFor={i.value}>{i.label} {i.default && "(default)"}</label>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <h6>Trend type</h6>
            <div className="btn-group" role="group">
              {options.trend_type.map(i => (
                <React.Fragment key={i.value}>
                  <input type="radio"
                         className="btn-check"
                         name="trend_type"
                         id={i.value}
                         value={i.value}
                         disabled={i.disabled}
                         checked={data.trend_type.value === i.value}
                         onChange={e => onChangeRadioBtn(e, i)}
                  />
                  <label className="btn btn-outline-primary btn-sm"
                         htmlFor={i.value}>{i.label} {i.default && "(default)"}</label>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <h6>Cohort displays</h6>
            <FormGroup className="mb-3">
              <Label htmlFor="">App</Label>
              <Select
                options={options.apps}
                value={data.app}
                onChange={e => setData(prev => ({ ...prev, app: e }))}
                placeholder="Select app..."
                classNamePrefix="select2-selection"
              />
            </FormGroup>
            <FormGroup className="">
              <Label htmlFor="">Grouped by</Label>
              {data.group_by.map((i, idx) => (
                <div className="d-flex justify-content-between align-items-center group-by-row mb-3" key={i.key}>
                  <div className="d-flex align-items-center">
                    <Select
                      value={i.value}
                      onChange={e => onChangeSelect(e, i, "group_by")}
                      // options={options.group_by}
                      options={getOptions('group_by', i)}
                      placeholder="Select group..."
                      classNamePrefix="select2-selection"
                      className="group-by-select me-3"
                    />
                    {(data.group_by.length - 1 === idx && data.group_by.length !== options.group_by.length) &&
                    <span
                      className="link-primary external-event d-inline-flex align-items-center justify-content-center"
                      onClick={() => handleAddRow("group_by")}>
                      <i className="bx bx-plus font-size-16 me-1" />Group by
                    </span>}
                  </div>
                  {data.group_by.length > 1 && <Button
                    onClick={e => handleRemoveRow("group_by", i)}
                    color="danger"
                    // outline
                    className="btn-block inner me-3"
                    style={{
                      height: "38px"
                    }}
                  >
                    <i className="bx bx-trash-alt font-size-18" />
                  </Button>}
                </div>
              ))}
            </FormGroup>
            <FormGroup className="">
              <Label htmlFor="">Filtered by</Label>
              {data.filter_by.map((i, idx) => (
                <div className="d-flex justify-content-between align-items-center group-by-row mb-3" key={i.key}>
                  <div className="d-flex align-items-center">
                    <div className="filter-by-select-container d-flex align-items-center">
                      <Select
                        value={i.value}
                        onChange={e => onChangeSelect(e, i, "filter_by")}
                        // options={options.group_by}
                        options={getOptions('filter_by', i)}
                        placeholder="Select..."
                        classNamePrefix="select2-selection"
                        className="group-by-select"
                      />
                      <span className="me-3 ms-3">is</span>
                      <DefaultMultipleSelect
                        options={
                          i.sub_options?.length > 0
                            ? i.sub_options.map(item => ({
                              key: item.value,
                              text: item.label,
                              checked: item.checked || false
                            }))
                            : []
                        }
                        defaultValue={i.sub_value}
                        className="group-by-select__multi me-3"
                        // filtered_by={this.props.filtered_by[key]}
                        // filtered_params={this.props.filtered_params}
                        isLoading={i.isLoading}
                        onChange={select => onSelectSubFilterBy(select, i)}
                        search
                      />
                    </div>
                    {(data.filter_by.length - 1 === idx && data.filter_by.length !== options.filter_by.length) &&
                    <span
                      className="link-primary external-event d-inline-flex align-items-center justify-content-center"
                      onClick={() => handleAddRow("filter_by")}>
                      <i className="bx bx-plus font-size-16 me-1" />Filter by
                    </span>}
                  </div>
                  <Button
                    onClick={e => handleRemoveRow("filter_by", i)}
                    color="danger"
                    // outline
                    className="btn-block inner me-3"
                    style={{
                      height: "38px"
                    }}
                  >
                    <i className="bx bx-trash-alt font-size-18" />
                  </Button>
                </div>
              ))}
              {data.filter_by.length === 0 &&
              <div>
                <span
                  className="link-primary external-event d-inline-flex align-items-center justify-content-center"
                  onClick={() => handleAddRow("filter_by")}>
                      <i className="bx bx-plus font-size-16 me-1" />Filter by
                    </span>
              </div>}
            </FormGroup>
          </div>
        </ModalBodyStyled>
        <ModalFooter>
          <Button color="primary" onClick={onSubmitModal}>Apply changes</Button>{" "}
          <Button color="secondary" onClick={onCloseModal}>Cancel</Button>
        </ModalFooter>
      </Modal>
      <CohortReportsSettingPanelStyled className="" onClick={() => setIsModalVisible(true)}>
        <CardBody className="d-flex justify-content-between align-items-center">
          <div className="d-flex">
            <div className="cohort-reports-setting-panel__item">
              <label>Cohort type</label>
              <span>{initialData.cohort_type.label}</span>
            </div>
            <div className="cohort-reports-setting-panel__item">
              <label>Trend type</label>
              <span>{initialData.trend_type.label}</span>
            </div>
            <div className="cohort-reports-setting-panel__item">
              <label>App</label>
              <span>{app_label}</span>
            </div>
            <div className="cohort-reports-setting-panel__item">
              <label>Grouped by</label>
              {initialData.group_by.map(i => <span key={i?.value || 0} className="me-1">{i?.label}</span>)}
            </div>
          </div>
          <div>
            <Button
              color="primary"
              outline
              onClick={() => setIsModalVisible(true)}
            >Edit cohort</Button>
          </div>
        </CardBody>
      </CohortReportsSettingPanelStyled>
    </React.Fragment>
  )
}

CohortReportsSettingPanel.propTypes = {
  initialData: PropTypes.any,
  options: PropTypes.any,
  onChange: PropTypes.func
}

const ModalBodyStyled = styled(ModalBody)`
  .group-by-row {

    .group-by-select {
      width: 160px;
    }
    
    .group-by-select__multi {
      width: 215px;
    }

    button {
      display: none;
    }

    &:hover {
      button {
        display: initial;
      }
    }
  }
`

const CohortReportsSettingPanelStyled = styled(Card)`

  cursor: pointer;
  transition: background-color .2s;

  &:hover {
    //background-color: rgba(80,165,241,.18);
    background-color: #dfeffc;
    //background-color: rgb(195 211 245 / 40%);
  }

  .btn-outline-primary:hover {
    background-color: inherit;
    color: #556ee6;
  }

  .cohort-reports-setting-panel__item {
    margin-right: 10px;

    label {
      margin-bottom: 0;
      margin-right: .5em;
    }

    span {
      color: #50a5f1;
      background-color: #dfeffc;
      padding: 2px 4px;
      border-radius: 50px;
    }
  }
`

export default (CohortReportsSettingPanel)
